/**
 * Hourly activity of the daily report. No chart library: plain bars.
 *
 * Only the service window is drawn (first to last busy hour, widened to eight
 * hours at least): twenty-four columns of which sixteen are empty would crush
 * the ones that matter. Every bar carries its value in its accessible name.
 */

import { formatMoney } from '@scan2eat/shared';
import type { ReportHourlyBucket } from '@scan2eat/shared';

import { labels } from '../labels.js';
import styles from './HourlyChart.module.css';

interface HourlyChartProps {
  hourly: ReportHourlyBucket[];
  currency: string;
}

export function HourlyChart({ hourly, currency }: HourlyChartProps) {
  const busy = hourly.filter((h) => h.orders > 0).map((h) => h.hour);
  let start = busy.length ? Math.max(0, Math.min(...busy) - 1) : 10;
  let end = busy.length ? Math.min(23, Math.max(...busy) + 1) : 22;
  while (end - start < 7) {
    if (start > 0) start -= 1;
    if (end - start < 7 && end < 23) end += 1;
  }

  const visible = hourly.filter((h) => h.hour >= start && h.hour <= end);
  const max = Math.max(...visible.map((h) => h.revenueMinor), 1);
  const peak = visible.reduce<ReportHourlyBucket | null>((best, h) => (!best || h.revenueMinor > best.revenueMinor ? h : best), null);

  return (
    <figure className={styles.figure}>
      <div className={styles.plot}>
        <div className={styles.grid} aria-hidden="true">
          <span>{formatMoney(max, currency)}</span>
          <span />
          <span />
          <span>0</span>
        </div>
        <div className={styles.bars}>
          {visible.map((h) => {
            const ratio = h.revenueMinor > 0 ? Math.max(h.revenueMinor / max, 0.03) : 0;
            const isPeak = peak !== null && peak.revenueMinor > 0 && h.hour === peak.hour;
            return (
              <div
                key={h.hour}
                className={styles.slot}
                role="img"
                aria-label={labels.report.hourLabel(h.hour, h.orders, formatMoney(h.revenueMinor, currency))}
                title={labels.report.hourLabel(h.hour, h.orders, formatMoney(h.revenueMinor, currency))}
              >
                <span
                  className={`${styles.bar} ${isPeak ? styles.peak : ''} ${h.orders === 0 ? styles.empty : ''}`}
                  style={{ height: `calc((100% - 1.5rem) * ${ratio.toFixed(4)})` }}
                />
                <span className={styles.hour}>{h.hour}h</span>
              </div>
            );
          })}
        </div>
      </div>
      {peak && peak.revenueMinor > 0 && (
        <figcaption className={styles.caption}>
          {labels.report.peak(peak.hour, peak.orders)}
        </figcaption>
      )}
    </figure>
  );
}
